<script>
import { BModal } from 'bootstrap-vue';
import { Alert } from '../../services/alert';
import dayjs from '../../core/dayjs';
import { setNewObjectToCreateChart } from '../../helpers/setNewObjectToCreateChart';
import { percentFormat } from '../../helpers/percentFormat';
import { currencyFormat } from '../../helpers/currencyFormat';

export default {
  components: {
    BModal,
  },

  props: ['title', 'source', 'range', 'timeperiod', 'timeseries', 'types'],

  data () {
    return {
      originalLinesState: [],
      deselectedLines: [],
      files: [],
      chartData: [],
      fetched: { lines: [], total: 0 },
      isFetchingData: false,
      isFetchingExportTypes: false,
      isExporting: false,
      selectedLines: [],
      exportTypes: [],
      selectedExportType: '',
      search: '',
      allCheckboxesState: 'none',
    };
  },

  async mounted () {
    Bus.$on('openExportModal', () => {
      this.openModal();

      this.fetch();
      this.getAvailableExportTypes();

    });
    Bus.$on('setNewObjectToCreateChart', (chartData) => setNewObjectToCreateChart(this.deselectedLines));
  },

  beforeDestroy () {
    Bus.$off('setNewObjectToCreateChart');
  },

  computed: {
    dateRangeText () {
      const range = this.getRange();

      const [from, to] = range.split(' > ');

      const formattedFrom = from.replaceAll('-', '/');
      const formattedTo = to.replaceAll('-', '/');

      return `Período de: ${formattedFrom} à ${formattedTo}`;
    },

    filteredLines () {
      return this.fetched.lines.filter((line) => {
        const lowerCaseSearch = this.search.toLowerCase();
        const lowerCaseLine = line.line ? line.line.toLowerCase() : '';
        const lowerCaseLabel = line.label ? line.label.toLowerCase() : '';

        return lowerCaseLine.includes(lowerCaseSearch)
          || lowerCaseLabel.includes(lowerCaseSearch);
      });
    },

    allCheckboxesStateText () {
      return this.allCheckboxesState === 'all' ? 'Todos' : 'Nenhum';
    }
  },

  methods: {
    openModal () {
      this.$refs['export-modal'].show();
    },

    hideModal () {
      this.$refs['export-modal'].hide();
    },

    textLeft (line) {
      return line.label ? line.label : line.line;
    },

    textRight (line) {
      return line.label ? line.line : '';
    },

    fetch () {
      const params = {
        range: this.range,
        timeseries: 0,
        timeperiod: this.timeperiod,
      };

      this.isFetchingData = true;

      this.$http.get(`/report/source/${this.source}/table`, { params })
        .then((response) => {
          const { data, options } = response.data;

          this.fetched = data;
          this.options = options;

          this.allCheckboxesState === 'all' ? this.unselectAllLines() : this.selectAllLines();
        })
        .catch(() => Alert.error('Erro ao fazer busca dos dados a serem exportados'))
        .finally(() => {
          this.isFetchingData = false;
        });
    },

    formatTotal (total) {
      if (! total) {
        return '-';
      }

      if (this.options.chart.currency) {
        return currencyFormat(total);
      }

      if (this.options.chart.percentage) {
        return percentFormat(total, 1);
      }

      return total;
    },

    getAvailableExportTypes () {
      this.isFetchingExportTypes = true;

      this.$http.get(`/export/${this.source}/types`)
        .then(({ data }) => {
          this.exportTypes = data.export_types;

          this.selectedExportType = this.exportTypes[0];
        }).catch((error) => {
          if (error.status === 404) {
            this.hideModal();

            return Alert.error('Esses dados não podem ser exportados');
          }

          return Alert.error('Erro ao buscar tipos de exportação disponíveis');
        }).finally(() => {
          this.isFetchingExportTypes = false;
        });
    },

    getRange () {
      if (!this.range) {
        const to = dayjs();
        const from = dayjs().subtract(14, 'day');

        return `${from.format('DD-MM-YYYY')} > ${to.format('DD-MM-YYYY')}`;
      }

      return this.range;
    },

    selectAllLines () {
      this.originalLinesState = [];
      this.selectedLines = this.fetched.lines.map((line) => {
        this.originalLinesState.push(line);
        return JSON.stringify(line)
      });
    },

    unselectAllLines () {
      this.selectedLines = [];
    },

    toggleCheckboxesState () {
      const state = this.allCheckboxesState;

      if (state === 'none') {
        this.unselectAllLines();
        return this.allCheckboxesState = 'all';
      }

      this.selectAllLines();
      return this.allCheckboxesState = 'none';
    },

    setDeselectedLines() {
      this.deselectedLines = [];
      this.originalLinesState.forEach((originalLineState) => {
        if (this.selectedLines.findIndex((selectedLine => {
          return JSON.parse(selectedLine).line === originalLineState.line
        })) < 0) {
          this.deselectedLines.push({
            name: originalLineState.line,
            total: originalLineState.total
          });
        }
        return;
      });
    },

    extractFilters () {
      return this.selectedLines.map((jsonLine) => {
        const line = JSON.parse(jsonLine);

        return line.filters;
      });
    },

    async handleSubmit (files = []) {
      this.isExporting = true;

      if (!this.validateForm()) {
        this.isExporting = false;

        return;
      }

      if (this.selectedExportType === 'pdf') {
        await this.submitPDFReportExport();
      } else {
        await this.exportReport(files);
      }

      this.isExporting = false;
    },

    async submitPDFReportExport() {
      this.setDeselectedLines();

      await this.getChartData()
    },

    validateForm() {
      if (this.selectedLines.length === 0) {
        Alert.error('É necessário ao menos um campo a ser exportado');
        return false;
      }

      if (!this.selectedExportType) {
        Alert.error('Por favor selecione o formato da exportação');
        return false;
      }

      return true;
    },

    async exportReport(files = []) {
      const filters = this.extractFilters();

      this.$http
        .post(`/export/${this.source}/${this.selectedExportType}`, {
          range: this.getRange(),
          timeperiod: this.timeperiod,
          filters: {
            in: filters,
          },
          files: files
        })
        .then(() => {
          Alert.success('Exportação em andamento');
        })
        .catch(() => {
          Alert.error('Algo deu errado ao exportar os seus dados');
        })
        .finally(() => {
          this.isExporting = false;

          this.hideModal();
      });
    },

    async getChartData() {
      this.isFetchingData = true;

      const params = {
        range: this.range,
        timeseries: this.timeseries ? 1 : 0,
        timeperiod: this.timeperiod,
        timeperiod_field: this.timeperiod_field || 'created_at',
        imports: this.imports ? 1 : 0,
      };

      this.$http.get(`/report/source/${this.source}`, { params }).then(({data: fetched}) => {
        this.chartData = setNewObjectToCreateChart(this.deselectedLines, fetched);
        Bus.$emit('send-deselecteds', this.deselectedLines);
        console.log("Emitting deselectedLines: ", this.deselectedLines);
        Bus.$emit('createCanvas', {callback: (img) => this.exportReport({chart: img}), fetched: this.chartData});
        this.isFetchingData = false;
      });
    },
  },
};
</script>

<template>
  <BModal
    id="export-modal"
    ref="export-modal"
    hide-footer="hide-footer"
    hide-header="hide-header"
    centered
  >
    <button
      class="close-button"
      type="button"
      aria-label="Fechar"
      v-on:click="hideModal"
    >
      <span aria-hidden="true">×</span>
    </button>

    <header class="d-flex flex-column justify-content-center align-items-center">
      <h2 class="export-modal-title">
        {{ title }}
      </h2>
      <p class="mb-0 text-salesflow-font-gray">{{ dateRangeText }}</p>
    </header>

    <form
      id="export-modal-form"
      @submit.prevent="handleSubmit"
    >
      <input
        id="export-modal-search-input"
        type="text"
        placeholder="Digite e pressione enter para pesquisar..."
        v-model="search"
      >

      <section class="w-100 d-flex flex-column justify-content-center align-items-center">
        <div class="w-100 d-flex justify-content-between align-items-center mb-2">
          <span class="export-modal-label">Selecione os dados que deseja exportar</span>
          <button
            id="toggle-export-options-button"
            type="button"
            @click="toggleCheckboxesState"
          >
            {{ allCheckboxesStateText }}
          </button>
        </div>
        <div class="export-items-container">
          <div class="export-items-list">
            <div v-if="isFetchingData" class="w-100 h-100 d-flex align-items-center justify-content-center">
              <i class="fa fa-circle-o-notch fa-spin" />
            </div>
            <div
              v-for="(line, index) of filteredLines"
              :key="index"
              class="export-item"
            >
              <input
                :id="`export-modal-line-${index}`"
                :value="JSON.stringify(line)"
                v-model="selectedLines"
                type="checkbox"
              >
              <span class="export-item-text ml-1">{{ textLeft(line) }}</span>
              <span class="export-item-text mx-auto">{{ textRight(line) }}</span>
              <span class="export-item-value">{{ formatTotal(line.total) }}</span>
            </div>
          </div>
        </div>
        <span class="ml-auto mt-2 mr-3 export-modal-label">Total: {{ formatTotal(fetched.total) }}</span>
      </section>

      <section class="w-100 d-flex flex-column justify-content-center">
        <label class="export-modal-label" for="export-type-select">Exportar relatório como</label>
        <select
          id="export-type-select"
          name="export-type-select"
          v-model="selectedExportType"
        >
          <option
            v-for="type of exportTypes"
            :value="type"
          >
            {{ type.toUpperCase() }}
          </option>
          <option
            value=""
            v-if="isFetchingExportTypes"
          >
            Carregando...
          </option>
        </select>
      </section>

      <button
        id="export-modal-button"
        class="salesflow-btn salesflow-btn-secondary"
        :disabled="isExporting"
      >
        Exportar
      </button>

      <button
        class="cancel-button"
        type="button"
        v-on:click="hideModal"
      >
        Cancelar
      </button>
    </form>

  </BModal>
</template>

<style lang="scss">
  @import '../../../sass/variables';
  @import '../../../sass/mixins';

  #export-modal {
    .modal-dialog {
      max-width: 540px;
      height: 100%;
      border-radius: 16px;
      overflow: hidden;
      margin: 0px auto;

      .modal-content {
        border-radius: 16px;
        overflow: hidden;
      }

      .modal-body {
        padding: 2rem 5rem 1.5rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        background-color: $salesflow-bg-content;

        #export-modal-form {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          padding: 0px;
          margin: 0px;
        }

        .export-modal-title {
          @include salesflow-title($font-weight: 500);
          margin-bottom: 0px;
        }

        #toggle-export-options-button {
          padding: 0px;
          margin: 0px;
          outline: 0px;
          border: 0px;
          background-color: transparent;

          @include salesflow-text($color: $salesflow-font-gray, $font-weight: 600);
        }

        #export-modal-search-input {
          width: 100%;
          padding: 0.75rem 1.5rem;
          background-color: $salesflow-bg-content;
          border: 1px solid $salesflow-outline-gray-medium;
          border-radius: 14px;
          outline: none;
          @include salesflow-text();

          &::placeholder {
            color: $salesflow-font-gray;
          }
        }

        .export-modal-label {
          @include salesflow-text($font-weight: 500);
        }

        .export-items-container {
          width: 100%;
          height: 232px;
          overflow: hidden;
          background-color: white;
          border: 1px solid $salesflow-outline-gray-medium;
          border-radius: 14px;
          padding-right: 0.25rem;
        }

        .export-items-list {
          padding: 0.5rem 0.75rem 0.5rem 1rem;
          width: 100%;
          height: 232px;
          overflow: auto;

          .export-item {
            width: 100%;
            display: flex;
            align-items: center;
            padding: 1rem 0px;

            &:not(:last-child) {
              border-bottom: 1px solid $salesflow-outline-gray-medium;
            }

            .export-item-text {
              @include salesflow-text;
            }

            .export-item-value {
              @include salesflow-text($color: $salesflow-font-gray);
            }
          }
        }

        #export-type-select {
          width: 100%;
          padding: 16px 12px;
          background-color: white;
          border: 1px solid $salesflow-outline-gray-medium;
          border-radius: 8px;

          @include salesflow-text($color: $salesflow-font-gray, $font-weight: 500);
        }

        #export-modal-button {
          max-width: fit-content;

          &:focus {
            outline: 0px;
          }

          &:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }
        }

        .cancel-button {
          padding: 0px;
          margin: 0px;
          border: 0px;
          outline: 0px;
          background-color: transparent;
          @include salesflow-text($color: $salesflow-font-gray, $font-weight: 600);
          text-decoration: underline;

          &:hover {
            color: $salesflow-purple-dark;
          }
        }

        .close-button {
          position: absolute;
          top: 16px;
          right: 18px;
          background-color: transparent;
          border: 0px;
          outline: 0px;
          padding: 0px;
          margin: 0px;

          font-size: 2rem;
          line-height: 1.5rem;
          color: $salesflow-font-gray;
          background-color: transparent;
        }
      }
    }
  }
</style>
